import {
  type BadgeColor,
  GridCellText,
  Icon,
  StatusBadge,
  type StatusBadgeTooltip,
  type TableColumn,
  Tooltip,
} from '@civilgrid/ui/components';
import { OPERATOR_TYPES } from '@civilgrid/ui/utils';
import { Link, useParams } from '@tanstack/react-router';
import type { CellContext } from '@tanstack/react-table';
import { isEmpty, isUndefined, kebabCase, uniq } from 'lodash';
import type { ReactNode } from 'react';
import { useSnapshot } from 'valtio';
import {
  downloadCSAttachments as apiDownloadCSAttachments,
  updateUtilityNote as apiUpdateUtilityNote,
} from '~/api/projects2';
import { getReasonForError } from '~/common/getReasonForError';
import { notifyDownloadError } from '~/common/notifyDownloadError';
import { notifyExportQueued } from '~/common/notifyExportQueued';
import { captureTypedPosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';
import { rowBadgeLabel } from '~/common/rowBadgeLabel';
import { type DataRequestV2, Outcome, RequestStatus, RequestType, UtilityPresence } from '~/common/types';
import { SendEmailModal } from '~/components2/SendEmailModal';
import FreeFormTextCell from '~/components2/Table/Cells/FreeFormTextCell';
import type { ProjectDetailsParams } from '../..';
import {
  compareAgencyRows,
  hasNoAssetsPresent,
  isConnectbaseCovered,
  LIVE_DATA_AVAILABLE_TOOLTIP,
  type UtilityAgency,
} from '../common';
import EstTimeCell from '../common/EstTimeCell';
import { makeEstTimeSortHeader } from '../common/EstTimeSortHeader';
import {
  type EstTimeRequest,
  type EstTimeState,
  getEstTimeState,
  getResponseConfidenceRank,
  surfacedEstimateDays,
} from '../common/estimatedResponseTime';
import { blocksNewRequest } from '../common/requestOnlyAgencies';
import store from '../store';
import AgencyNameCell from './AgencyNameCell';
import DocumentsCell from './DocumentsCell';
import { getUtilityRowId, isSubGeometryParentRow, requestMatchesRow } from './grouping';
import LatestUpdateCell from './LatestUpdateCell';
import StartDownload from './StartDownload';

type Cell = CellContext<UtilityAgency, any>;

const rowName = (a: UtilityAgency) => `${a.agencyName} ${a.type}`;

const LIVE_MAP_LABELS = {
  digitized: 'Yes - Digitized',
  yes: 'Yes',
  no: 'No',
  noAssetsPresent: 'No Assets Present',
} as const;

const LIVE_MAP_FILTER_OPTIONS = Object.values(LIVE_MAP_LABELS).map((label) => ({ value: label, label }));

/** The Live Map label a row shows, in the order the cell checks; search, filter and sort read it too. */
const liveMapLabel = (a: UtilityAgency, connectbaseCoverageEnabled: boolean) => {
  if (a.isDigitized) return LIVE_MAP_LABELS.digitized;
  if (hasNoAssetsPresent(a)) return LIVE_MAP_LABELS.noAssetsPresent;
  if (a.availableDigitally || isConnectbaseCovered(a, connectbaseCoverageEnabled)) return LIVE_MAP_LABELS.yes;
  return LIVE_MAP_LABELS.no;
};

const renderAvailabilityDigital = (cell: Cell) => {
  // Subscribes this cell, so it repaints when the coverage flag resolves after the first render.
  const { connectbaseCoverageEnabled } = useSnapshot(store) as typeof store;
  const a = cell.row.original;
  // On a summary parent, agency-level columns (Asset/Live Map/Download) stay visible while collapsed
  // and blank once expanded (the Region children carry them then).
  if (isSubGeometryParentRow(a) && cell.row.getIsExpanded()) return null;
  const noAssetsPresent = hasNoAssetsPresent(a);

  const label = (status: string) => rowBadgeLabel('Live Map', status, rowName(a));

  if (a.isDigitized) {
    return (
      <StatusBadge
        color="green"
        aria-label={label('Yes - Digitized')}
        data-testid="live-map-cell"
        tooltip={{
          title: 'Digitized Assets',
          description: 'Assets have been digitized for this project by CivilGrid.',
        }}
      >
        <GridCellText>Yes - Digitized</GridCellText>
      </StatusBadge>
    );
  }

  if (noAssetsPresent) {
    return (
      <StatusBadge
        aria-label={label('No Assets Present')}
        data-testid="live-map-cell"
        tooltip={{
          title: 'No Assets Present',
          description:
            'Based on the digital data in our platform, there are no assets present in the project boundary.',
        }}
        color="green"
      >
        <GridCellText>No Assets Present</GridCellText>
      </StatusBadge>
    );
  }

  if (a.availableDigitally) {
    return (
      <StatusBadge
        aria-label={label('Yes')}
        data-testid="live-map-cell"
        tooltip={{
          title: 'Assets Present',
          description: 'Digital data with layer attributes is available on the map.',
        }}
        color="green"
      >
        <GridCellText>Yes</GridCellText>
      </StatusBadge>
    );
  }

  if (isConnectbaseCovered(a, connectbaseCoverageEnabled)) {
    return (
      <StatusBadge
        aria-label={label('Yes')}
        data-testid="live-map-connectbase-cell"
        tooltip={LIVE_DATA_AVAILABLE_TOOLTIP}
        color="green"
      >
        <GridCellText>Yes</GridCellText>
      </StatusBadge>
    );
  }

  return (
    <StatusBadge
      aria-label={label('No')}
      data-testid="live-map-cell"
      tooltip={{
        title: 'Data Not Available',
        description: 'Digital data NOT available on the map currently. CivilGrid can request maps on your behalf.',
      }}
      color="grey"
    >
      <GridCellText>No</GridCellText>
    </StatusBadge>
  );
};

const getRequest = (agencyId: string, type: RequestType, subGeometryId?: string | null) => {
  return Object.values(store.dataRequests).find(
    (r) => r.type === type && requestMatchesRow(r, { agencyId, agencySubGeometryId: subGeometryId }),
  );
};

const OpenDrawerLink = ({
  drawer,
  children,
  onClick,
  'data-testid': dataTestId,
}: {
  drawer: string;
  children: ReactNode;
  onClick?: () => void;
  'data-testid'?: string;
}) => {
  return (
    <Link
      to="/projects/$projectId/$tabId"
      params={{ projectId: store.project?.id ?? '', tabId: 'utilities' }}
      search={{ drawer }}
      className="text-blue-50 underline hover:text-blue-60"
      onClick={onClick}
      data-testid={dataTestId}
    >
      {children}
    </Link>
  );
};

export const renderRequestStatus = (
  requestStatus: RequestStatus,
  utilityPresence?: UtilityPresence,
  outcome?: Outcome,
) => {
  let color: BadgeColor = 'grey';
  let tooltip: StatusBadgeTooltip = {
    title: 'Contact Support',
    description: 'There is an issue with this request, please contact Support.',
  };
  let trigger = 'Contact Support';
  if (requestStatus === RequestStatus.Pending) {
    color = 'blue';
    tooltip = {
      title: 'Pending',
      description: 'Your request has been received. We will review it shortly.',
    };
    trigger = 'Pending';
  } else if (
    requestStatus === RequestStatus.InProgress0 ||
    requestStatus === RequestStatus.InProgress1 ||
    requestStatus === RequestStatus.InProgress2 ||
    requestStatus === RequestStatus.InProgress3
  ) {
    color = 'blue';
    tooltip = {
      title: 'In Progress',
      description: 'Your request is currently in progress.',
    };
    trigger = 'In Progress';
  } else if (requestStatus === RequestStatus.ActionRequired) {
    color = 'yellow';
    tooltip = {
      title: 'Action Required',
      description: (
        <span>
          Please see recent <OpenDrawerLink drawer="project-channel">chat messages</OpenDrawerLink>.
        </span>
      ),
    };
    trigger = 'Action Required';
  } else if (requestStatus === RequestStatus.OnHoldPerAgency) {
    color = 'blue';
    tooltip = {
      title: 'On Hold (Per Agency)',
      description: <span>Agency has indicated they require additional time to complete the request.</span>,
    };
    trigger = 'On Hold (Per Agency)';
  } else if (requestStatus === RequestStatus.Cancelled) {
    color = 'grey';
    tooltip = {
      title: 'Cancelled',
      description: (
        <span>
          <OpenDrawerLink drawer="project-channel">Contact us</OpenDrawerLink> if you have any questions.
        </span>
      ),
    };
    trigger = 'Cancelled';
  } else if (requestStatus === RequestStatus.Dismissed) {
    color = 'grey';
    tooltip = {
      title: 'Dismissed',
      description: (
        <span>
          <OpenDrawerLink drawer="project-channel">Contact us</OpenDrawerLink> if you have any questions.
        </span>
      ),
    };
    trigger = 'Dismissed'; // 👈 this case should never occur in theory
  } else if (requestStatus === RequestStatus.Complete) {
    if (utilityPresence === UtilityPresence.Present) {
      if (outcome === Outcome.AgencyNotSharing) {
        color = 'yellow';
        trigger = 'Complete - Present, Agency will not share documents';
        tooltip = {
          title: 'Request Complete - Assets Present',
          description: 'Agency has confirmed that utilities are present but will not share documents.',
        };
      } else if (outcome === Outcome.DocumentsReceived) {
        color = 'green';
        trigger = 'Complete - Assets Present, Documents Received';
        tooltip = {
          title: 'Request Complete - Utilities Present',
          description: 'Agency has confirmed that utilities are present and documents have been received.',
        };
      }
    } else if (utilityPresence === UtilityPresence.NoAssetsPresent) {
      if (outcome === Outcome.AgencyResponded) {
        color = 'green';
        trigger = 'Complete - NAP';
        tooltip = {
          title: 'Request Complete - No Assets Present',
          description: 'Agency has confirmed that no assets are present in project area',
        };
      } else if (outcome === Outcome.ReferredToOtherAgency) {
        color = 'green';
        trigger = 'Complete - See Note';
        tooltip = {
          title: 'Referred to another agency',
          description: 'Agency told us to contact another agency. We initiated another request under a new agency.',
        };
      }
    } else if (utilityPresence === UtilityPresence.PresenceUnknown) {
      if (outcome === Outcome.AgencyUnresponsive) {
        color = 'yellow';
        trigger = 'Complete - See Note';
        tooltip = {
          title: 'Agency Unresponsive',
          description: 'Agency did not respond to our requests. We reached out 4 times.',
        };
      } else if (outcome === Outcome.AgencyNotSharing) {
        color = 'yellow';
        trigger = 'Complete - See Note';
        tooltip = {
          title: 'Agency Will Not Share',
          description: 'Agency policy not to share data.',
        };
      } else if (outcome === Outcome.ReferredToOtherAgency) {
        color = 'yellow';
        trigger = 'Complete - See Note';
        tooltip = {
          title: 'Referred to another agency',
          description: 'Agency told us to contact another agency. We initiated another request under a new agency.',
        };
      }
    } else if (utilityPresence === UtilityPresence.OutsideServiceArea) {
      color = 'green';
      trigger = 'Complete - See Note';
      tooltip = {
        title: 'Outside Service Area',
        description: 'Agency confirmed project boundary is outside their service area.',
      };
    }
  }

  return { color, tooltip, trigger };
};

const renderAvailabilityRecordsResearch = (cell: Cell) => {
  // Subscribes this cell, so it repaints when the coverage flag resolves after the first render.
  const { connectbaseCoverageEnabled } = useSnapshot(store) as typeof store;
  // when making changes here, also update Records Research Report job
  const cellType = cell.column.id as RequestType;
  const a = cell.row.original;
  if (isSubGeometryParentRow(a)) return null;
  const noAssetsPresent = hasNoAssetsPresent(a);
  const request = getRequest(a.agencyId, cellType, a.agencySubGeometryId);

  let testId: string | undefined;
  // 👇 these defaults should never be visible to the user
  let color: BadgeColor = 'grey';
  let tooltip: StatusBadgeTooltip = {
    title: 'Contact Support',
    description: 'There is an issue with this request, please contact Support.',
  };
  let trigger = 'Contact Support';

  if (request) {
    // has already been requested, display progress status
    ({ color, tooltip, trigger } = renderRequestStatus(
      request.requestStatus,
      request.utilityPresence,
      request.outcome,
    ));
  } else if (blocksNewRequest(a, request)) {
    trigger = '-';
    color = 'grey';
    tooltip = {
      title: 'Not Available',
      description: 'This agency is not in the project agency list, so a new request cannot be started here.',
    };
  } else {
    // not requested yet, display requestable or not
    if (cellType === RequestType.MapsRequested) {
      if (a.isPublished) {
        if (a.type === 'Fiber') {
          trigger = 'Request';
          tooltip = {
            title: 'Request Maps',
            description: (
              <span>
                Our fiber data generally includes only the trunk lines running in the public right-of-way. If you need
                more detail, you can request fiber detail maps from the agencies. Generally, cable and copper lines are
                not digitized.
              </span>
            ),
          };
          color = 'yellow';
        } else {
          if (noAssetsPresent) {
            color = 'green';
            trigger = 'N/A';
            tooltip = {
              title: 'Not Applicable',
              description:
                'Based on the digital data in our platform, there are no assets present in the project boundary.',
            };
          } else {
            color = 'grey';
            tooltip = {
              title: 'Not Applicable',
              description: 'Digital data available.',
            };
            trigger = 'N/A';
          }
        }
      } else if (isConnectbaseCovered(a, connectbaseCoverageEnabled)) {
        // A stored Connectbase route reads like published digital data: no maps request is offered.
        trigger = 'N/A';
        color = 'grey';
        testId = 'map-request-connectbase-cell';
        tooltip = { title: 'Not Applicable', description: 'Digital data available.' };
      } else {
        trigger = `Request`;
        color = 'yellow';
        tooltip = {
          title: 'Request Maps',
          description: (
            <span>
              Maps are available for request. Click <OpenDrawerLink drawer="request-data">here</OpenDrawerLink> to
              start.
            </span>
          ),
        };
      }
    } else if (cellType === RequestType.AsbuiltsRequested) {
      if (noAssetsPresent) {
        trigger = 'N/A';
        color = 'green';
        tooltip = {
          title: 'Not Applicable',
          description:
            'Based on the digital data in our platform, there are no assets present in the project boundary.',
        };
      } else if (a.asbuiltsAvailable) {
        trigger = 'Request';
        color = 'yellow';
        tooltip = {
          title: 'Request As-builts',
          description: (
            <span>
              As-builts are available for request. Click <OpenDrawerLink drawer="request-data">here</OpenDrawerLink> to
              start.
            </span>
          ),
        };
      } else {
        trigger = '-';
        tooltip = {
          title: 'Not Available',
          description: 'As-builts not available for this agency.',
        };
        color = 'grey';
      }
    }
  }

  const isAsbuilts = cellType === RequestType.AsbuiltsRequested;
  return (
    <StatusBadge
      tooltip={tooltip}
      color={color}
      aria-label={rowBadgeLabel(isAsbuilts ? 'As-Builts' : 'Map', trigger, rowName(a), tooltip.title)}
      data-testid={testId ?? (isAsbuilts ? 'asbuilt-request-cell' : 'map-request-cell')}
    >
      <GridCellText>{trigger}</GridCellText>
    </StatusBadge>
  );
};

const availableForDownloadTooltip = (a: UtilityAgency): StatusBadgeTooltip => ({
  title: 'Available For Download',
  description: (
    <div>
      {a.type} data is available for download. <StartDownload />
    </div>
  ),
});

const renderDownloadCell = (cell: Cell) => {
  // Subscribes this cell, so it repaints when the coverage flag resolves after the first render.
  const { connectbaseCoverageEnabled } = useSnapshot(store) as typeof store;
  const a = cell.row.original;
  if (isSubGeometryParentRow(a) && cell.row.getIsExpanded()) return null;
  const noAssetsPresent = hasNoAssetsPresent(a);

  // // 👇 these defaults should never be visible to the user
  let color: BadgeColor = 'grey';
  let tooltip: StatusBadgeTooltip = {
    title: 'Contact Support',
    description: 'There is an issue with this request, please contact Support.',
  };
  let trigger = 'Contact Support';
  let testId: string | undefined;

  if (a.isDigitized) {
    trigger = 'Yes - Digitized';
    color = 'green';
    tooltip = {
      title: 'Digitized Data Available for Download',
      description: 'Digitized assets for this project are available for download.',
    };
  } else if (noAssetsPresent) {
    trigger = 'N/A';
    color = 'green';
    tooltip = {
      title: 'Not Applicable',
      description: 'Based on the digital data in our platform, there are no assets present in the project boundary.',
    };
  } else if (a.isPublished) {
    if (a.applicableRestrictions && !isEmpty(a.applicableRestrictions)) {
      trigger = 'Approval Required';
      color = 'yellow';
      tooltip.title = 'Approval Required For Download';

      if (a.applicableRestrictions.urban === 0 || a.applicableRestrictions.rural === 0) {
        tooltip.description = (
          <div>
            {a.agencyName} requires manual approval from provider prior to download. <StartDownload />
          </div>
        );
      } else {
        tooltip.description = (
          <div>
            {a.agencyName} requires manual approval from provider for projects greater than{' '}
            {a.applicableRestrictions.urban !== undefined ? (
              <>{a.applicableRestrictions.urban} acres in urban areas</>
            ) : (
              <>{a.applicableRestrictions.rural} acres in rural areas</>
            )}
            . <StartDownload />
          </div>
        );
      }
    } else {
      trigger = 'Yes';
      color = 'green';
      tooltip = availableForDownloadTooltip(a);
    }
  } else if (isConnectbaseCovered(a, connectbaseCoverageEnabled)) {
    // Downloads never apply the 6 and 40 acre fiber limit, so a covered route gets no approval state.
    trigger = 'Yes';
    color = 'green';
    testId = 'download-connectbase-cell';
    tooltip = availableForDownloadTooltip(a);
  } else {
    trigger = '-';
    color = 'grey';
    tooltip = {
      title: 'Digital Data Not Available',
      description: <div>{a.type} data is NOT available for download at this time.</div>,
    };
  }

  return (
    <StatusBadge
      color={color}
      tooltip={tooltip}
      aria-label={rowBadgeLabel('Download', trigger, rowName(a), tooltip.title)}
      data-testid={testId ?? 'download-cell'}
    >
      <GridCellText>{trigger}</GridCellText>
    </StatusBadge>
  );
};

const renderDocumentsCell = (cell: Cell) => {
  const a = cell.row.original;
  if (isSubGeometryParentRow(a)) return null;

  return <DocumentsCell agency={a} />;
};

const renderAssetType = (cell: Cell) => {
  const a = cell.row.original;
  if (isSubGeometryParentRow(a) && cell.row.getIsExpanded()) return null;
  return <GridCellText>{a.type}</GridCellText>;
};

const renderLatestUpdate = (cell: Cell) => {
  const a = cell.row.original;
  if (isSubGeometryParentRow(a)) return null;

  return <LatestUpdateCell agency={a} />;
};

// For group bands, which render their header as-is and so can't use meta.headerTooltip.
const renderHeaderWithTooltip = (title: string, tooltip: ReactNode) => {
  return (
    <div className="flex items-center space-x-1">
      <div>{title}</div>
      <Tooltip
        text={tooltip}
        aria-label={`About ${title}`}
        delayDuration={200}
        sideOffset={10}
        className="max-w-[30rem]"
        triggerClassName="align-middle"
        data-testid={`info-tooltip-${kebabCase(`About ${title}`)}`}
      >
        {/* Matches the info icon the grid puts on leaf headers via meta.headerTooltip. */}
        <Icon icon="infoCircled" size={12} />
      </Tooltip>
    </div>
  );
};

// Custom sortable header: clicking cycles est time → confidence → off, instead of the default asc/desc
// toggle. Shared with the Select Assets step; only the test ids and PostHog `source` differ.
const renderEstTimeHeader = makeEstTimeSortHeader({ testIdPrefix: 'utilities', source: 'table' });

// The requests that belong to a row. A summary parent stands in for all of its Region children, so it
// reads every request for the agency; every other row matches only the requests in its own record.
const rowRequests = (a: UtilityAgency): EstTimeRequest[] =>
  Object.values(store.dataRequests).filter((r) =>
    isSubGeometryParentRow(a) ? r.agency.agencyId === a.agencyId : requestMatchesRow(r, a),
  );

const estTimeState = (a: UtilityAgency) => getEstTimeState(a, rowRequests(a));

// Kinds whose cell carries text rather than a bare dash. `requested`, `delivered` and `completed`
// qualify on the request alone, so they count even on the project-agencies-job-off payload, which
// omits responseStats.
const CELL_HAS_CONTENT: ReadonlySet<EstTimeState['kind']> = new Set([
  'estimate',
  'requested',
  'delivered',
  'completed',
]);

/**
 * Whether any agency row (or sub-row) has something other than a bare dash to show. Drives hiding the
 * whole column when every cell would be a dash (killswitch on, or nothing in flight and no usable stats).
 */
export const anyAgencyHasEstTime = (rows: readonly UtilityAgency[]): boolean =>
  rows.some((row) => {
    if (CELL_HAS_CONTENT.has(estTimeState(row).kind)) return true;
    return row.subRows ? anyAgencyHasEstTime(row.subRows) : false;
  });

const renderEstTime = (cell: Cell) => (
  <EstTimeCell state={estTimeState(cell.row.original)} testId="utilities-est-time-cell" />
);

/** Gives the Asset filter this project's asset types, since `agencies.type` is free text with no fixed list. */
export const withAssetTypeOptions = (columns: TableColumn<UtilityAgency>[], rows: UtilityAgency[]) => {
  const assetTypes = uniq(rows.map((row) => row.type)).sort();
  const options = assetTypes.map((type) => ({ value: type, label: type }));

  return columns.map((column) => {
    if (column.id !== 'type') return column;
    const filterValueConfig = { type: 'select' as const, options };
    return { ...column, meta: { ...column.meta, filterValueConfig } };
  });
};

const agencyNameColumn = (size: number): TableColumn<UtilityAgency> => ({
  id: 'agencyName',
  header: 'Agency',
  size,
  accessorKey: 'agencyName',
  cell: AgencyNameCell,
  meta: { sortCompare: compareAgencyRows },
});

export const compactColumns: TableColumn<UtilityAgency>[] = [
  agencyNameColumn(200),
  {
    id: 'type',
    header: 'Asset',
    accessorKey: 'type',
    size: 95,
    cell: renderAssetType,
    // Filter by exact match; withAssetTypeOptions adds the dropdown's choices from the project's rows.
    meta: { filterOperatorTypeConfig: OPERATOR_TYPES.EXACT },
  },
  {
    id: 'availability',
    enableColumnFilter: false,
    header: 'Availability/Status',
    meta: {
      headerTooltip:
        'For digital data (highlighted in green), shows confirmed presence or absence based on available data. For request data (highlighted in yellow or blue), shows status of records request.',
    },
    size: 250,
    cell: (cell) => {
      // Subscribes this cell, so it repaints when the coverage flag resolves after the first render.
      const { connectbaseCoverageEnabled } = useSnapshot(store) as typeof store;
      const a = cell.row.original;
      if (isSubGeometryParentRow(a)) return null;

      const noAssetsPresent = hasNoAssetsPresent(a);
      const mapRequest = getRequest(a.agencyId, RequestType.MapsRequested, a.agencySubGeometryId);
      const asBuiltsRequest = getRequest(a.agencyId, RequestType.AsbuiltsRequested, a.agencySubGeometryId);

      if (mapRequest || asBuiltsRequest) {
        const statuses = [mapRequest?.requestStatus, asBuiltsRequest?.requestStatus].filter((s) => !isUndefined(s));

        if (statuses.every((s) => s === RequestStatus.Complete)) {
          return (
            <StatusBadge color="green">
              <GridCellText>Complete</GridCellText>
            </StatusBadge>
          );
        } else if (statuses.every((s) => s === RequestStatus.Cancelled || s === RequestStatus.Dismissed)) {
          // a mix of the two reads as cancelled, matching the per-request chip on the details page
          const trigger = statuses.some((s) => s === RequestStatus.Cancelled) ? 'Cancelled' : 'Dismissed';
          return (
            <StatusBadge color="grey">
              <GridCellText>{trigger}</GridCellText>
            </StatusBadge>
          );
        }

        return (
          <StatusBadge color="blue">
            <GridCellText>View Request</GridCellText>
          </StatusBadge>
        );
      } else if (a.isDigitized) {
        return (
          <StatusBadge color="green">
            <GridCellText>Yes - Digitized</GridCellText>
          </StatusBadge>
        );
      } else if (noAssetsPresent) {
        return (
          <StatusBadge color="green">
            <GridCellText>No Assets Present</GridCellText>
          </StatusBadge>
        );
      } else if (a.availableDigitally) {
        return (
          <StatusBadge color="green">
            <GridCellText>Available Digitally</GridCellText>
          </StatusBadge>
        );
      } else if (isConnectbaseCovered(a, connectbaseCoverageEnabled)) {
        return (
          <StatusBadge color="green" data-testid="availability-connectbase-cell">
            <GridCellText>Available Digitally</GridCellText>
          </StatusBadge>
        );
      } else {
        return (
          <StatusBadge color="yellow">
            <GridCellText>Request</GridCellText>
          </StatusBadge>
        );
      }
    },
  },
];

class AttachmentsExportError extends Error {}

/** Extracted so the attachments-download failure toast can be exercised without rendering the column header. */
export const downloadAllAttachments = async (projectId: string, ccEmails?: string[]) => {
  try {
    const res = await apiDownloadCSAttachments(projectId, ccEmails);

    if (!res.success) {
      throw new AttachmentsExportError('Error downloading attachments');
    }

    notifyExportQueued({
      title: 'Downloading Attachments',
      message: 'Your attachments will be emailed to you',
      ccEmails,
    });
    captureTypedPosthogEvent(POSTHOG_EVENTS.projectUtilitiesAttachmentsDownloaded, {
      project_id: projectId,
      cc_recipient_count: ccEmails?.length ?? 0,
    });
  } catch (err) {
    const reason = err instanceof AttachmentsExportError ? 'api_error' : getReasonForError(err);
    captureTypedPosthogEvent(POSTHOG_EVENTS.projectUtilitiesAttachmentsDownloadFailed, {
      project_id: projectId,
      reason,
    });
    notifyDownloadError();
    return false;
  }
};

/** Whether CivilGrid has received any file for one of the project's agencies: "Email all utility files" only shows then. */
export const hasAgencyFiles = (
  agencies: Record<string, UtilityAgency>,
  dataRequests: Record<string, DataRequestV2>,
) => {
  const agencyIds = new Set(Object.values(agencies).map((a) => a.agencyId));
  return Object.values(dataRequests).some((d) => agencyIds.has(d.agency.agencyId) && d.files.length > 0);
};

/** The email dialog behind "Email all utility files" in the table menu and the Exports menu. */
export function DownloadAllFilesModal({
  open,
  onOpenChange,
  onCloseAutoFocus,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCloseAutoFocus?: (event: Event) => void;
}) {
  const { projectId } = useParams({ strict: false }) as ProjectDetailsParams;
  return (
    <SendEmailModal
      open={open}
      onOpenChange={onOpenChange}
      title="Email all utility files"
      testIdPrefix="civilsearch-attachments"
      onCloseAutoFocus={onCloseAutoFocus}
      onSend={(ccEmails) => downloadAllAttachments(projectId, ccEmails.length > 0 ? ccEmails : undefined)}
    />
  );
}

export const expandedColumns: TableColumn<UtilityAgency>[] = [
  agencyNameColumn(250),
  {
    id: 'type',
    header: 'Asset',
    accessorKey: 'type',
    size: 90,
    cell: renderAssetType,
    // Filter by exact match; withAssetTypeOptions adds the dropdown's choices from the project's rows.
    meta: { filterOperatorTypeConfig: OPERATOR_TYPES.EXACT },
  },
  {
    id: 'digitalAvailability',
    header: () =>
      renderHeaderWithTooltip(
        'Digital Availability',
        <>
          Digital data is GIS data that can be viewed on the CivilGrid map and downloaded to CAD or GIS. This data is
          direct-sourced from agencies/owners and is routinely updated per the <i>Latest Update</i> column.
        </>,
      ),
    size: 75,
    columns: [
      {
        id: 'availableDigitally',
        // Room for the label, info icon, and the sort arrow and column menu the header shows on hover.
        size: 135,
        header: 'Live Map',
        meta: {
          headerTooltip:
            'Digital map data availability and/or presence. Hover over the text for a more detailed explanation.',
          filterValueConfig: { type: 'select', options: LIVE_MAP_FILTER_OPTIONS },
          filterOperatorTypeConfig: OPERATOR_TYPES.EXACT,
        },
        accessorFn: (a) => liveMapLabel(a, store.connectbaseCoverageEnabled),
        cell: renderAvailabilityDigital,
      },
      {
        id: 'download',
        enableColumnFilter: false,
        header: 'Download',
        meta: {
          headerTooltip:
            'Digital map data is typically available for download to CAD or GIS. Area limits or approvals may be required for certain asset types. Hover over the text for details.',
        },
        cell: renderDownloadCell,
        size: 125,
      },
    ],
  },
  {
    id: 'recordsResearchStatus',
    header: () =>
      renderHeaderWithTooltip(
        'Records Research Status',
        'Request Maps and as-builts through our premium CivilSearch service. CivilGrid will handle utility requests on your behalf, managing the entire process from start to finish.',
      ),
    columns: [
      {
        id: RequestType.MapsRequested,
        enableColumnFilter: false,
        header: 'Map',
        meta: {
          headerTooltip:
            'Depicts Request Status, Utility Presence, and/or Request Outcome of ongoing records research requests for map PDFs. Initiate a new request by clicking the Request Research button at the top right.',
        },
        cell: renderAvailabilityRecordsResearch,
        size: 210,
      },
      {
        id: RequestType.AsbuiltsRequested,
        enableColumnFilter: false,
        header: 'As-Builts',
        meta: {
          headerTooltip:
            'Depicts Request Status, Utility Presence, and/or Request Outcome of ongoing records research requests for as-builts. Initiate a new request by clicking the Request Research button at the top right.',
        },
        cell: renderAvailabilityRecordsResearch,
        size: 210,
      },
    ],
  },
  {
    id: 'attachments',
    header: 'Files',
    // Sorts rows with files first, then rows without. Rows without files have no label.
    accessorFn: (a) => (a.hasFiles ? 'Files available' : ''),
    meta: {
      headerTooltip: 'Files received by CivilGrid from agencies.',
      sortCompare: (a: UtilityAgency, b: UtilityAgency) => Number(b.hasFiles) - Number(a.hasFiles),
      // "Does Not Equal" finds the rows without files.
      filterValueConfig: { type: 'select', options: [{ value: 'Files available', label: 'Files available' }] },
      filterOperatorTypeConfig: OPERATOR_TYPES.EXACT,
    },
    cell: renderDocumentsCell,
    size: 115,
  },
  {
    id: 'latestUpdates',
    enableColumnFilter: false,
    header: 'Latest Update',
    meta: {
      headerTooltip: (
        <div>
          Latest activity and updates for data source dates and records research requests.
          <br />
          [D] = Digital data - refresh dates and cadences
          <br />
          [M] = Map PDF requests
          <br />
          [AB] = As-Built requests
        </div>
      ),
    },
    cell: renderLatestUpdate,
    size: 230,
  },
  {
    id: 'utilityNotes',
    size: 200,
    header: 'Notes',
    enableSorting: true,
    enableColumnFilter: true,
    // Like the Conflicts Summary notes: hidden until turned on from the columns menu, filterable as text.
    meta: {
      defaultVisible: false,
      filterValueConfig: { type: 'text' },
      filterOperatorTypeConfig: OPERATOR_TYPES.TEXT,
      headerTooltip: (
        <div>
          <div>Use this section to store quick notes.</div>
          <br />
          <div>
            Note: This is a shared space, i.e. users in your organization with access to this project can add/delete
            contents.
          </div>
        </div>
      ),
    },
    cell: (cell) => {
      const { projectId } = useParams({ strict: false }) as { projectId: string };
      const agency = cell.row.original;
      if (isSubGeometryParentRow(agency)) return null;
      // CG-1219: a request-only row has no projectUtilityNotes record, so there is no id to
      // commit an edit against.
      if (agency.isRequestOnly) return <div data-testid="utilities-notes-request-only">—</div>;

      async function onCommit(notes: string) {
        const res = await apiUpdateUtilityNote(projectId, agency.utilityNotes.id, notes);

        if (!res.id) throw new Error('Bad response!');
        const note = store.agencies[agency.id].utilityNotes;

        store.agencies[agency.id].utilityNotes = {
          ...note,
          ...res,
        };
      }

      // Full-width wrapper, as on the Conflicts Summary notes column (its `CellShell`): the editor sizes its
      // focus box from its parent, so this makes it fill the cell.
      return (
        <div className="w-full">
          <FreeFormTextCell
            initialValue={cell.row.original.utilityNotes?.notes ?? ''}
            onCommit={onCommit}
            data-testid={`utilities-notes-value-${getUtilityRowId(agency)}`}
            inputTestId={`utilities-notes-input-${getUtilityRowId(agency)}`}
          />
        </div>
      );
    },
    // Lets search and filters match note text; the cell still edits through FreeFormTextCell.
    accessorFn: (a) => a.utilityNotes?.notes ?? '',
  },
];

/**
 * The Est time column and its two hidden sort-only helper columns. Kept but not in `expandedColumns` for now:
 * its header is a component (the time → confidence → off sort cycle), and ClientTable only renders text headers.
 */
export const estTimeColumns: TableColumn<UtilityAgency>[] = [
  {
    id: 'estTime',
    // Display-only: sorting is driven by the hidden estTimeDays / estTimeConfidence columns via the
    // custom header, so TableV2 does not add its own asc/desc arrow or click handler on this column.
    enableSorting: false,
    size: 110,
    header: renderEstTimeHeader,
    cell: renderEstTime,
  },
  {
    // Hidden helper column backing the "sort by est time" mode. Raw days (numeric); undefined rows last.
    id: 'estTimeDays',
    accessorFn: (a) => surfacedEstimateDays(estTimeState(a)) ?? undefined,
    sortUndefined: 'last',
    enableHiding: true,
    // Never rendered → zero layout width. TableV2's auto-resize subtracts every column's size (hidden
    // included) from the available width; a default-sized hidden column steals ~150px.
    size: 0,
    minSize: 0,
  },
  {
    // Hidden helper column backing the "sort by confidence" mode. Never rendered (visibility forced off);
    // ranks only rows that surface a range, others sort last.
    id: 'estTimeConfidence',
    accessorFn: (a) => (surfacedEstimateDays(estTimeState(a)) === null ? undefined : getResponseConfidenceRank(a)),
    sortUndefined: 'last',
    enableHiding: true,
    // Zero-width for the same reason as estTimeDays above.
    size: 0,
    minSize: 0,
  },
];
